import { useTeamMembers } from '@/hooks/useTeamMembers'
import { Trash } from '@mynaui/icons-react'
import { DialogPortal } from '@radix-ui/react-dialog'
import UserProfileDialogTeamInvitees from './UserProfileDialogTeamInvitees'
import { Stack, Text } from './custom-components'
import { Button } from './shadcn/button'
import { Dialog, DialogContent } from './shadcn/dialog'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from './shadcn/select'
import { Skeleton } from './shadcn/skeleton'

const UserProfileDialogTeamsMemberListSection = ({ teamId }: { teamId: string }) => {
  const {
    open,
    onOpenChange,
    memberIdToRemove,
    setMemberIdToRemove,
    teamMembers,
    isLoadingTeamMembers,
    user,
    currentUserCanEditTeamStuff,
    changeTeamMemberRole,
    isChangingMemberRole,
    removeTeamMember,
    isRemovingTeamMember,
    isSwitchingAccount,
    leaveTeam,
    isLeavingTeam,
  } = useTeamMembers()

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={isLeavingTeam || isRemovingTeamMember || isSwitchingAccount ? undefined : onOpenChange}
      >
        <DialogPortal>
          <DialogContent
            className={
              'max-w-md w-full p-6 gap-6 flex flex-col items-center justify-center bg-themebackground dark:bg-themebackgrounddark'
            }
          >
            <Text size={'xxl'} weight={'semibold'} className="w-fit">
              Warning! 🚨
            </Text>
            <Text size={'lg'} weight={'normal'} className="text-thememutedforeground w-fit">
              {memberIdToRemove
                ? 'You’re removing a member – are you sure?'
                : 'You’re removing yourself from this team – are you sure?'}
            </Text>

            <Button
              variant={'destructive'}
              className="w-full"
              loading={isLeavingTeam || isRemovingTeamMember || isSwitchingAccount}
              disabled={isLeavingTeam || isRemovingTeamMember || isSwitchingAccount}
              onClick={() => (memberIdToRemove ? removeTeamMember({ memberId: memberIdToRemove }) : leaveTeam())}
            >
              Confirm
            </Button>
          </DialogContent>
        </DialogPortal>
      </Dialog>

      <Stack className="p-4 gap-4 rounded-2xl border border-themesidebarborder dark:border-themesidebarborderdark overflow-hidden">
        <Text size={'sm'} weight={'medium'}>
          People with access
        </Text>
        {!isLoadingTeamMembers && !teamMembers ? null : (
          <Stack className="gap-4">
            {isLoadingTeamMembers
              ? [...Array(5)].map(() => (
                  <div className="flex justify-between items-center gap-10 w-full">
                    <div className="flex items-center gap-3 w-full">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <Stack className="gap-2 w-full">
                        <Skeleton className="h-4 w-full max-w-[250px]" />
                        <Skeleton className="h-4 w-full max-w-[200px]" />
                      </Stack>
                    </div>
                  </div>
                ))
              : teamMembers
                ? teamMembers.map((member) => (
                    <div className="flex justify-between items-center gap-10">
                      <div className="flex items-center gap-3">
                        <img className="min-w-8 w-8 h-8 rounded-full" src={member.avatar ?? '/bg_gradient.jpg'} />
                        <Text size={'sm'} weight={'medium'}>
                          {member.firstName} {member.lastName}
                        </Text>
                      </div>

                      <Select
                        onValueChange={(value: 'creator' | 'commenter') => {
                          changeTeamMemberRole({
                            memberId: member.memberId,
                            newRole: value,
                          })
                        }}
                        value={member.role}
                        disabled={
                          (!currentUserCanEditTeamStuff && member.id !== user?.id) ||
                          isChangingMemberRole ||
                          member.role === 'owner'
                        }
                      >
                        <SelectTrigger className="w-fit">
                          <SelectValue placeholder={member.role} />
                        </SelectTrigger>
                        {
                          <SelectContent>
                            <SelectGroup>
                              {member.role === 'owner' ? (
                                <SelectItem value="owner" disabled>
                                  Owner
                                </SelectItem>
                              ) : null}
                              <SelectItem value="creator" disabled={member.id === user?.id}>
                                Creator
                              </SelectItem>
                              <SelectItem value="commenter" disabled={member.id === user?.id}>
                                Commenter
                              </SelectItem>
                              <div
                                onClick={
                                  member.id === user?.id
                                    ? () => {
                                        setMemberIdToRemove(null)
                                        onOpenChange(true)
                                      }
                                    : () => {
                                        setMemberIdToRemove(member.id)
                                        onOpenChange(true)
                                      }
                                }
                                className="flex items-center gap-1 p-1.5 !text-sm cursor-pointer hover:bg-slate-100 dark:hover:text-slate-50 rounded-md text-red-500"
                              >
                                <Trash className="max-w-4" /> {member.id === user?.id ? 'Leave Team' : 'Remove User'}
                              </div>
                            </SelectGroup>
                          </SelectContent>
                        }
                      </Select>
                    </div>
                  ))
                : null}
          </Stack>
        )}
        <UserProfileDialogTeamInvitees teamId={teamId} />
      </Stack>
    </>
  )
}

export default UserProfileDialogTeamsMemberListSection
